// Isolated hook for updating existing workspaces

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UpdateWorkspaceRequest } from '@/types/workspace';
import { FailureRecord } from '@/types/failure';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { workspacesApi } from '@/services/api/workspacesApi';
import { useCurrentUserId } from '@/hooks/useCurrentUserId';

// Import query keys from the main workspace hooks file
import { WORKSPACE_QUERY_KEYS } from './useWorkspaces';

/**
 * Hook for updating an existing workspace
 */
export function useUpdateWorkspace() {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();

  return useMutation({
    mutationFn: async ({
      id,
      request,
      currentFailures,
      currentResults,
    }: {
      id: string;
      request: UpdateWorkspaceRequest;
      currentFailures?: FailureRecord[] | unknown[]; // Support both FailureRecord and VblRecord
      currentResults?: WeibullAnalysisResult | unknown; // Support both WeibullAnalysisResult and VblAnalysisResult
    }) => {
      return workspacesApi.update(id, request, currentFailures, currentResults, userId);
    },
    onSuccess: (data, variables) => {
      // Update the workspace in cache
      queryClient.setQueryData(WORKSPACE_QUERY_KEYS.detail(variables.id), {
        workspace: data,
        warnings: [],
      });

      // Invalidate workspace lists and versions
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.versions(variables.id) });
    },
  });
}
